import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, BookOpen, Utensils, PartyPopper, Coffee, Play, Cross, Shield, Award } from 'lucide-react';
import AnimatedHeading from '../components/AnimatedHeading';
import VideoModal from '../components/VideoModal';
import HdVideo from '../components/HdVideo';
import { getImpactStats, getVideos } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

// Animated Counter Component
const AnimatedCounter = ({ target, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return <span ref={counterRef}>{count}</span>;
};

const Homepage = () => {
  const [stats, setStats] = useState(null);
  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, videosData] = await Promise.all([
          getImpactStats(),
          getVideos(null, true)
        ]);
        setStats(statsData);
        setFeaturedVideos(videosData.slice(0, 2));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="homepage">
      {/* Hero Section - WOW Factor */}
      <section className="relative min-h-screen pb-16 md:pb-0 flex items-center justify-center overflow-hidden pt-20 md:pt-0" data-testid="hero-section">
        {/* Animated Background Layers */}
        <div className="absolute inset-0">
          {/* Base gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950"></div>
          
          {/* Animated gradient orbs */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-600/30 rounded-full filter blur-[120px] animate-pulse"></div>
            <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-yellow-500/20 rounded-full filter blur-[120px] animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full filter blur-[150px]"></div>
          </div>
          
          {/* Background image with overlay */}
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1920')] bg-cover bg-center"></div>
          </div>
          
          {/* Grid pattern overlay for depth */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          {/* Animated Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8 animate-fadeIn">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-white/80 text-sm font-medium">{language === 'en' ? 'Saving Lives in Colombia' : 'Salvando Vidas en Colombia'}</span>
          </div>

          {/* Main Headline - Staggered Animation */}
          <h1 className="mb-8" data-testid="hero-headline">
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white leading-[1.1] tracking-tight animate-slideUp" style={{animationDelay: '0.1s'}}>
              {language === 'en' ? 'Rescuing Children.' : 'Rescatando Niños.'}
            </span>
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[1.1] tracking-tight animate-slideUp" style={{animationDelay: '0.3s'}}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-400 to-yellow-500">{language === 'en' ? 'Rebuilding Hope.' : 'Reconstruyendo Esperanza.'}</span>
            </span>
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white leading-[1.1] tracking-tight animate-slideUp" style={{animationDelay: '0.5s'}}>
              {language === 'en' ? 'Restoring Futures.' : 'Restaurando Futuros.'}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl lg:text-2xl mb-4 max-w-4xl mx-auto text-slate-300 leading-relaxed animate-fadeIn" style={{animationDelay: '0.7s'}} data-testid="hero-subheadline">
            {language === 'en' 
              ? 'At "The Safe House" Children\'s Rescue Center in Colombia, we stand in the gap for children in the most vulnerable communities by providing protection, healing, and opportunity for a new transformed future.'
              : 'En "The Safe House" Centro de Rescate Infantil en Colombia, nos interponemos por los niños en las comunidades más vulnerables, brindando protección, sanación y oportunidad para un nuevo futuro transformado.'}
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fadeIn" style={{animationDelay: '0.9s'}}>
            <Link
              to="/donate"
              className="group relative px-10 py-5 bg-gradient-to-r from-yellow-500 to-amber-500 text-slate-900 rounded-full font-bold text-lg overflow-hidden transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_40px_rgba(234,179,8,0.4)]"
              data-testid="hero-donate-button"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                <Heart className="w-5 h-5" />
                {t('hero.donateToday', language)}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </Link>
          </div>
        </div>
      </section>

      {/* Sound of Freedom - Inspired Mission */}
      <section className="pt-12 pb-0 bg-white" data-testid="sound-of-freedom-section">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">

            {/* 100+ Children Counter - Main Focal Point */}
            <div className="flex justify-center mb-12">
              <div className="bg-gradient-to-br from-colombia-yellow via-amber-400 to-amber-500 rounded-3xl px-12 py-8 shadow-2xl transform hover:scale-105 transition-transform">
                <div className="text-center">
                  <div className="text-7xl md:text-8xl font-black text-gray-900 mb-2">
                    <AnimatedCounter target={100} duration={2500} />+
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-gray-800 uppercase tracking-wide">
                    {language === 'en' ? 'Children Helped Weekly' : 'Niños Ayudados Semanalmente'}
                  </div>
                </div>
              </div>
            </div>

            {/* Our Mission - Cinematic Full-Width Design */}
            <div className="relative mb-0 -mx-4 md:-mx-8 lg:-mx-16">
              {/* Full-width background */}
              <div className="relative overflow-hidden">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0">
                  <img 
                    src="https://images.unsplash.com/photo-1719935769846-e41568d49dc0?w=1920&q=80" 
                    alt="Children" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/65"></div>
                </div>

                <div className="relative z-10 py-20 md:py-28 px-4 md:px-8 lg:px-16">
                  <div className="max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                      <span className="text-colombia-yellow text-2xl md:text-3xl font-black tracking-wider uppercase">{t('mission.title', language)}</span>
                      <div className="h-1 w-20 bg-colombia-yellow mt-4 mx-auto"></div>
                    </div>

                    {/* Main Mission Statement */}
                    <div className="text-center mb-12">
                      <p className="text-xl md:text-2xl lg:text-3xl text-white font-light leading-relaxed max-w-4xl mx-auto">
                        {t('mission.mainStatement', language)}
                      </p>
                    </div>

                    {/* Three Pillars */}
                    <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12">
                      <div className="text-center p-6 border border-white/10 rounded-2xl bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all">
                        <div className="text-colombia-yellow text-4xl font-bold mb-3">{t('mission.restore', language)}</div>
                        <p className="text-white/80 text-lg">{t('mission.restoreText', language)}</p>
                      </div>
                      <div className="text-center p-6 border border-white/10 rounded-2xl bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all">
                        <div className="text-colombia-yellow text-4xl font-bold mb-3">{t('mission.strengthen', language)}</div>
                        <p className="text-white/80 text-lg">{t('mission.strengthenText', language)}</p>
                      </div>
                      <div className="text-center p-6 border border-white/10 rounded-2xl bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all">
                        <div className="text-colombia-yellow text-4xl font-bold mb-3">{t('mission.equip', language)}</div>
                        <p className="text-white/80 text-lg">{t('mission.equipText', language)}</p>
                      </div>
                    </div>

                    {/* Transformation Statement */}
                    <div className="text-center mb-12">
                      <div className="inline-block">
                        <p className="text-lg md:text-xl text-white/60 mb-2">{t('mission.notShortTerm', language)}</p>
                        <p className="text-2xl md:text-3xl text-white font-semibold">{t('mission.thisIs', language)} <span className="text-colombia-yellow">{t('mission.lastingTransformation', language)}</span></p>
                      </div>
                    </div>

                    {/* Three Bold Statements */}
                    <div className="flex flex-col items-center gap-4 mb-12">
                      <div className="flex items-center gap-4">
                        <div className="h-px w-8 md:w-16 bg-white/30"></div>
                        <p className="text-xl md:text-2xl text-white/90 font-light">{t('mission.weAreRebuilding', language)}</p>
                        <div className="h-px w-8 md:w-16 bg-white/30"></div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="h-px w-8 md:w-16 bg-white/30"></div>
                        <p className="text-xl md:text-2xl text-white/90 font-light">{t('mission.weAreBreaking', language)}</p>
                        <div className="h-px w-8 md:w-16 bg-white/30"></div>
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <div className="h-px w-8 md:w-16 bg-colombia-yellow/50"></div>
                        <p className="text-2xl md:text-3xl text-white font-medium">{t('mission.weAreRaising', language)} <span className="font-black text-transparent bg-clip-text bg-gradient-to-r from-colombia-yellow via-amber-400 to-yellow-300">{t('mission.worldChangers', language)}</span></p>
                        <div className="h-px w-8 md:w-16 bg-colombia-yellow/50"></div>
                      </div>
                    </div>

                    {/* Closing Statement */}
                    <div className="text-center">
                      <p className="text-lg md:text-xl lg:text-2xl text-white/80 font-light italic max-w-3xl mx-auto">
                        {t('mission.closingStatement', language)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New homepage video - between Mission section and "This Is the Mission in Action" */}
      <section className="relative pt-2 pb-8 overflow-hidden bg-white">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-200/30 rounded-full filter blur-[100px]"></div>
        <div className="relative z-10">
          <div className="flex justify-center px-1.5 md:px-4">
            <div className="relative group w-full md:max-w-4xl">
              <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500 hidden md:block"></div>
              <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-1.5 md:p-3 rounded-xl md:rounded-2xl shadow-2xl">
                <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-lg md:rounded-xl">
                  <div className="bg-gray-950 p-1 md:p-1.5 rounded-lg md:rounded-xl">
                    <div className="rounded-md md:rounded-lg overflow-hidden">
                      <HdVideo uid="291a49b646270663c7fea905637e5fc7" aspectClass="aspect-video" testId="video-world-changers" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video D23CFBCC - Before "This Is Who We Fight For" */}
      <section className="relative py-12 overflow-hidden">
        {/* Warm subtle background */}
        <div className="absolute inset-0 bg-gradient-to-b from-stone-100 via-stone-200 to-stone-100"></div>
        {/* Soft ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-200/30 rounded-full filter blur-[100px]"></div>
        
        <div className="relative z-10 container mx-auto px-4">
          {/* Text above video */}
          <div className="text-center mb-8">
            <p className="text-amber-700 text-xs font-bold tracking-[0.3em] uppercase mb-3">
              {language === 'en' ? 'See It for Yourself' : 'Véalo Usted Mismo'}
            </p>
            <p className="text-3xl md:text-4xl font-bold text-gray-900 leading-snug">
              {language === 'en' ? <>This Is the Mission<br />in Action.</> : <>Esta Es la Misión<br />en Acción.</>}
            </p>
          </div>
          <div className="flex justify-center max-w-6xl mx-auto">
            {/* Video with premium shadow frame */}
            <div className="relative group flex-shrink-0">
              {/* Dramatic drop shadow */}
              <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500"></div>
              {/* Outer matte frame */}
              <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-3 rounded-2xl shadow-2xl">
                {/* Thin inset highlight */}
                <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-xl">
                  <div className="bg-gray-950 p-1.5 rounded-xl">
                    <div className="rounded-lg overflow-hidden w-72 md:w-[22rem]">
                      <HdVideo uid="4803330bd5a11582eca2108451eb27c5" testId="video-d23cfbcc" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "This Is Who We Fight For" Photo - After Our Mission */}
      <section className="pt-4 pb-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-400/30 via-colombia-yellow/20 to-orange-400/30 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_638fb05c-6d2c-4d67-9ab1-8229e78d8a01/artifacts/ll1dgrzf_Old%20group%20photo.jpg" 
                    alt="The Safe House community" 
                    className="w-full h-auto brightness-105 contrast-105 saturate-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-blue-500/10 mix-blend-overlay"></div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-10">
                  <div>
                    <p className="text-lg md:text-4xl lg:text-5xl font-black text-white mb-1 md:mb-2">
                      {language === 'en' ? 'This Is' : 'Por Ellos'} <span className="text-colombia-yellow">{language === 'en' ? 'Who We Fight For.' : 'Luchamos.'}</span>
                    </p>
                    <p className="text-white/90 text-xs md:text-lg">{t('fightFor.subtitle', language)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="relative py-20 overflow-hidden" data-testid="why-we-exist-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/15 rounded-full filter blur-[150px]"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-colombia-yellow/10 rounded-full filter blur-[130px]"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/5 rounded-full filter blur-[150px]"></div>
        </div>
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}></div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="text-center mb-12">
            <AnimatedHeading
              level="h2"
              className="text-4xl md:text-5xl text-white mb-6"
              variant="slide"
              center={true}
              accent={true}
            >
              {language === 'en' ? 'Why We Exist' : 'Por Qué Existimos'}
            </AnimatedHeading>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {language === 'en' 
                ? <>In the shadows of Colombia's most dangerous neighborhoods, children face unthinkable threats daily — sex trafficking, organ harvesting, brutal violence. <strong className="text-colombia-yellow">The Safe House exists because every child deserves protection, not predators. Safety, not survival mode. A future, not fear.</strong></>
                : <>En las sombras de los barrios más peligrosos de Colombia, los niños enfrentan amenazas impensables a diario — tráfico sexual, extracción de órganos, violencia brutal. <strong className="text-colombia-yellow">The Safe House existe porque cada niño merece protección, no depredadores. Seguridad, no modo de supervivencia. Un futuro, no miedo.</strong></>}
            </p>
          </div>

          {/* Our Model - Six Pillars */}
          <div className="max-w-5xl mx-auto mb-12">
            <h3 className="text-2xl font-bold text-white text-center mb-8">{language === 'en' ? 'Our Model: Six Pillars of Transformation' : 'Nuestro Modelo: Seis Pilares de Transformación'}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { icon: <Cross className="w-6 h-6" />, title: language === 'en' ? 'Faith' : 'Fe' },
                { icon: <Shield className="w-6 h-6" />, title: language === 'en' ? 'Safety' : 'Seguridad' },
                { icon: <BookOpen className="w-6 h-6" />, title: language === 'en' ? 'Education' : 'Educación' },
                { icon: <Utensils className="w-6 h-6" />, title: language === 'en' ? 'Nutrition' : 'Nutrición' },
                { icon: <PartyPopper className="w-6 h-6" />, title: language === 'en' ? 'Celebration' : 'Celebración' },
                { icon: <Heart className="w-6 h-6" />, title: language === 'en' ? 'Mentorship' : 'Mentoría' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 text-center transform hover:scale-105 hover:bg-white/10 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="w-12 h-12 bg-colombia-yellow rounded-full flex items-center justify-center mx-auto mb-3 text-gray-900">
                    {item.icon}
                  </div>
                  <h4 className="text-white font-semibold text-sm uppercase tracking-wide">{item.title}</h4>
                </div>
              ))}
            </div>
            {/* See More Details Button */}
            <div className="text-center mt-8">
              <Link
                to="/about#six-pillars"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-colombia-yellow hover:text-gray-900 border border-white/20 hover:border-colombia-yellow rounded-full text-white font-semibold transition-all duration-300 group"
                data-testid="see-pillars-details-btn"
              >
                {language === 'en' ? 'See More Details' : 'Ver Más Detalles'}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>

          {/* Closing Statement */}
          <div className="text-center">
            <p className="text-2xl font-bold text-white mb-2">
              {language === 'en' ? 'We do not just provide aid.' : 'No solo proporcionamos ayuda.'}
            </p>
            <p className="text-xl text-colombia-yellow font-semibold">
              {language === 'en' ? 'We provide belonging. We provide hope. We provide a future.' : 'Proporcionamos pertenencia. Proporcionamos esperanza. Proporcionamos un futuro.'}
            </p>
          </div>
        </div>
      </section>

      {/* Our Calling Section */}
      <section className="py-14 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              {/* Background with gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-sh-dark via-sh-mid to-sh-dark rounded-3xl"></div>
              
              <div className="relative z-10 p-8 md:p-16">
                {/* Main Heading with Glow Effect */}
                <div className="text-center mb-12">
                  <div className="inline-block mb-6">
                    <span className="px-4 py-2 bg-colombia-yellow/20 border border-colombia-yellow/30 rounded-full text-colombia-yellow text-sm font-semibold tracking-wider uppercase">
                      {language === 'en' ? 'Our Calling' : 'Nuestro Llamado'}
                    </span>
                  </div>
                  <h2 className="text-4xl md:text-6xl font-black text-white mb-4 leading-tight">
                    {language === 'en' ? 'Driven by' : 'Impulsados por'} <span className="text-colombia-yellow">{language === 'en' ? 'Purpose.' : 'Propósito.'}</span>
                  </h2>
                  <h2 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
                    {language === 'en' ? 'Called to' : 'Llamados a'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-300">{language === 'en' ? 'Protect.' : 'Proteger.'}</span>
                  </h2>
                </div>

                {/* Content Grid */}
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Left: Main Message */}
                  <div className="space-y-6">
                    <p className="text-xl md:text-2xl text-gray-200 leading-relaxed text-center">
                      {language === 'en' 
                        ? <>The Children's Rescue Center is a <span className="text-white font-semibold">calling in action!</span></>
                        : <>El Centro de Rescate Infantil es un <span className="text-white font-semibold">¡llamado en acción!</span></>}
                    </p>
                    
                    {/* Feature Cards */}
                    <div className="grid grid-cols-2 gap-4 my-8">
                      {[
                        { icon: '🏠', label: language === 'en' ? 'Safe Refuge' : 'Refugio Seguro' },
                        { icon: '🍽️', label: language === 'en' ? 'Daily Meals' : 'Comidas Diarias' },
                        { icon: '📚', label: language === 'en' ? 'Education' : 'Educación' },
                        { icon: '💚', label: language === 'en' ? 'Trauma Healing' : 'Sanación del Trauma' }
                      ].map((item, idx) => (
                        <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all duration-300 hover:scale-105 cursor-default">
                          <span className="text-2xl mb-2 block">{item.icon}</span>
                          <span className="text-white text-sm font-medium">{item.label}</span>
                        </div>
                      ))}
                    </div>

                    <p className="text-lg text-gray-300 leading-relaxed">
                      {language === 'en'
                        ? <>It's more than a shelter — it is a refuge of <span className="text-colombia-yellow font-semibold">safety, love, and transformation</span> where children develop character through Bible principles.</>
                        : <>Es más que un refugio — es un lugar de <span className="text-colombia-yellow font-semibold">seguridad, amor y transformación</span> donde los niños desarrollan carácter a través de principios bíblicos.</>}
                    </p>
                  </div>

                  {/* Right: Tagline + Movie Poster + Quote */}
                  <div className="space-y-6">
                    {/* Tagline - Before Picture */}
                    <div className="text-center">
                      <p className="text-2xl md:text-3xl font-bold text-white mb-2">
                        {language === 'en' ? 'Here, hope is restored.' : 'Aquí, la esperanza se restaura.'}
                      </p>
                      <p className="text-2xl md:text-3xl font-bold text-colombia-yellow mb-6">
                        {language === 'en' ? 'Identity is rebuilt. Futures are rewritten.' : 'La identidad se reconstruye. Los futuros se reescriben.'}
                      </p>
                    </div>

                    {/* Movie Poster with Glow */}
                    <div className="flex justify-center">
                      <div className="relative group">
                        <div className="absolute -inset-2 bg-gradient-to-r from-colombia-yellow/50 to-amber-500/50 rounded-2xl blur-xl opacity-40 group-hover:opacity-60 transition-opacity duration-500"></div>
                        <div className="relative max-w-[200px] rounded-xl overflow-hidden shadow-2xl border-2 border-white/20">
                          <img
                            src="https://www.vmissions.org/wp-content/uploads/Screenshot-2024-03-14-at-2.52.26%E2%80%AFPM-1200x1548.png"
                            alt="Sound of Freedom - Inspired Mission"
                            className="w-full"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                          <div className="absolute bottom-3 left-3 right-3 text-white text-center">
                            <p className="text-xs font-bold tracking-wider uppercase">{language === 'en' ? 'The Mission is Real' : 'La Misión es Real'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Founder Quote - Under Picture */}
                    <div className="relative mt-2">
                      <blockquote className="border-l-4 border-colombia-yellow pl-5 py-2">
                        <p className="text-base md:text-lg text-gray-200 italic leading-relaxed mb-3">
                          {language === 'en'
                            ? '"After watching Sound of Freedom, God stirred something deep in my heart, igniting a calling to stand in the gap for the most vulnerable children in Colombia and to build a place where they could be rescued, protected, and fully restored."'
                            : '"Después de ver Sound of Freedom, Dios removió algo profundo en mi corazón, encendiendo un llamado a interponerme por los niños más vulnerables de Colombia y construir un lugar donde pudieran ser rescatados, protegidos y completamente restaurados."'}
                        </p>
                        <footer className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-colombia-yellow to-amber-600 flex items-center justify-center text-white font-bold text-xs">AG</div>
                          <div>
                            <span className="text-white font-semibold text-sm">Angelo Gennaro</span>
                            <span className="text-gray-400 text-sm ml-2">— {language === 'en' ? 'Founder' : 'Fundador'}</span>
                          </div>
                        </footer>
                      </blockquote>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Army Video + Photo - Under Angelo Quote */}
            <div className="mt-12">
              <div className="grid md:grid-cols-[1fr_auto_1fr] gap-4 items-center">
                {/* Army Video on the left */}
                <div className="flex justify-center">
                  <div className="relative group flex-shrink-0">
                    <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500"></div>
                    <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-3 rounded-2xl shadow-2xl">
                      <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-xl">
                        <div className="bg-gray-950 p-1.5 rounded-xl">
                          <div className="rounded-lg overflow-hidden w-[17rem] md:w-[21rem]">
                            <HdVideo uid="188e06699b9a1e4881b8ce1b7de35c45" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Focal Point Statement Between Video and Photo */}
                <div className="flex items-center justify-center py-4 md:py-0 px-2">
                  <p className="text-xl md:text-2xl lg:text-3xl font-black text-white text-center leading-snug tracking-wide">
                    {language === 'en' 
                      ? <>Rescuing children from sex trafficking &amp; organ harvesting. <span className="text-colombia-yellow">God's children aren't for sale!</span></>
                      : <>Rescatando niños del tráfico sexual y la extracción de órganos porque <span className="text-colombia-yellow">los hijos de Dios no están a la venta.</span></>}
                  </p>
                </div>
                {/* Army Photo on the right */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-sm mx-auto">
                  <img
                    src="https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/fc06a210-534c-4c04-f0e6-42a333104f00/public"
                    alt="Safe House with military support"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>

            {/* Our Impact - Transformation Journey */}
            <div className="mt-12 bg-sh-dark rounded-3xl p-8 md:p-12">
              <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">{language === 'en' ? 'Our Impact' : 'Nuestro Impacto'}</h3>
              
              {/* Impact Journey - Horizontal Flow */}
              <div className="relative mb-10">
                {/* Connecting Line */}
                <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-colombia-yellow to-transparent transform -translate-y-1/2 opacity-30"></div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(language === 'en' ? [
                    { number: '01', label: 'RESCUED', desc: 'From danger & trafficking' },
                    { number: '02', label: 'RESTORED', desc: 'Identity & healing' },
                    { number: '03', label: 'EDUCATED', desc: 'Knowledge & skills' },
                    { number: '04', label: 'EMPOWERED', desc: 'Future leaders' }
                  ] : [
                    { number: '01', label: 'RESCATADOS', desc: 'Del peligro y el tráfico' },
                    { number: '02', label: 'RESTAURADOS', desc: 'Identidad y sanación' },
                    { number: '03', label: 'EDUCADOS', desc: 'Conocimiento y habilidades' },
                    { number: '04', label: 'EMPODERADOS', desc: 'Futuros líderes' }
                  ]).map((step, index) => (
                    <div key={index} className="relative group">
                      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center transform group-hover:bg-white/10 transition-all duration-300">
                        <div className="text-colombia-yellow text-xs font-mono mb-1">{step.number}</div>
                        <div className="text-lg md:text-xl font-black text-white mb-1 tracking-tight">{step.label}</div>
                        <div className="text-gray-400 text-xs">{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Closing Statement */}
              <div className="text-center">
                <p className="text-lg md:text-xl text-white font-light">
                  {language === 'en' ? "A child's story doesn't have to end in tragedy." : 'La historia de un niño no tiene que terminar en tragedia.'}
                </p>
                <p className="text-lg md:text-xl text-colombia-yellow font-bold mt-1">
                  {language === 'en' ? 'Their future can be rewritten.' : 'Su futuro puede ser reescrito.'}
                </p>
              </div>

              {/* See the Video Stories - Cinema-themed CTA */}
              <div className="flex justify-center mt-10">
                <Link
                  to="/media"
                  className="group relative inline-flex items-center gap-3 px-8 py-4 bg-black border-2 border-colombia-yellow/60 rounded-lg overflow-hidden transition-all duration-500 hover:border-colombia-yellow hover:shadow-[0_0_30px_rgba(252,209,22,0.3)]"
                  data-testid="see-video-stories-button"
                >
                  {/* Film strip decoration - left */}
                  <div className="absolute left-0 top-0 bottom-0 w-5 bg-gray-900 flex flex-col justify-around items-center py-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-2.5 h-1.5 bg-gray-700 rounded-[1px]"></div>
                    ))}
                  </div>
                  {/* Film strip decoration - right */}
                  <div className="absolute right-0 top-0 bottom-0 w-5 bg-gray-900 flex flex-col justify-around items-center py-1">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="w-2.5 h-1.5 bg-gray-700 rounded-[1px]"></div>
                    ))}
                  </div>
                  {/* Animated play icon */}
                  <div className="relative z-10 ml-4 w-10 h-10 rounded-full bg-colombia-yellow flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-5 h-5 text-gray-900 ml-0.5" fill="currentColor" />
                    <div className="absolute inset-0 rounded-full bg-colombia-yellow/50 animate-ping opacity-0 group-hover:opacity-60"></div>
                  </div>
                  {/* Text */}
                  <div className="relative z-10 mr-4">
                    <span className="block text-white font-bold text-base tracking-wide group-hover:text-colombia-yellow transition-colors">{language === 'en' ? 'See the stories, watch now' : 'Mira las historias, ver ahora'}</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* "This is what hope looks like" Photo - Before The Real Heroes */}
      <section className="relative py-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800 via-slate-900 to-slate-800"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-colombia-yellow rounded-full filter blur-[150px]"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-amber-500 rounded-full filter blur-[120px]"></div>
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img 
                src="https://customer-assets.emergentagent.com/job_638fb05c-6d2c-4d67-9ab1-8229e78d8a01/artifacts/hao9zubp_IMG_2493.jpg" 
                alt="The Safe House community celebration" 
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 text-white">
                <p className="text-sm md:text-2xl font-bold">{t('hope.title', language)}</p>
                <p className="text-white/80 text-xs md:text-base">{t('hope.subtitle', language)}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Children Group Photo */}
      <section className="relative py-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img 
                src="https://imagedelivery.net/Q8eMLw5VGzUJNe6L3eCY_A/c91f4c04-d0de-4534-41f1-e0e85ad2e700/public" 
                alt="The Safe House children" 
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Video 455C60F4 - Before "The Real Dangers" */}
      <section className="relative py-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800 via-slate-900 to-gray-900"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/10 rounded-full filter blur-[120px]"></div>
        <div className="relative z-10">
          <div className="flex justify-center px-1.5 md:px-4">
            <div className="relative group w-full md:max-w-4xl">
              <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500 hidden md:block"></div>
              <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-1.5 md:p-3 rounded-xl md:rounded-2xl shadow-2xl">
                <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-lg md:rounded-xl">
                  <div className="bg-gray-950 p-1 md:p-1.5 rounded-lg md:rounded-xl">
                    <div className="rounded-md md:rounded-lg overflow-hidden">
                      <HdVideo uid="455c60f48f6bf8beef80dddddf789a08" aspectClass="aspect-video" testId="video-before-real-dangers" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Real Dangers */}
      <section className="relative py-10 overflow-hidden" data-testid="life-on-edge-section">
        {/* Dark dramatic background */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-slate-800 to-gray-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-red-500 rounded-full filter blur-[150px]"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-700 rounded-full filter blur-[150px]"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <AnimatedHeading
            level="h2"
            className="text-4xl md:text-5xl text-white mb-12"
            variant="slide"
            center={true}
            accent={true}
          >
            {language === 'en' ? 'The Real Dangers' : 'Los Peligros Reales'}
          </AnimatedHeading>
          {/* Video directly under section title */}
          <div className="mb-12 flex justify-center">
            <div className="relative group flex-shrink-0">
              <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500"></div>
              <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-3 rounded-2xl shadow-2xl">
                <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-xl">
                  <div className="bg-gray-950 p-1.5 rounded-xl">
                    <div className="rounded-lg overflow-hidden w-80 sm:w-96 md:w-[28rem] lg:w-[32rem]">
                      <HdVideo uid="3c3e31bced4f1720e1894be7593e6dbe" aspectClass="aspect-[1078/1452]" testId="video-real-dangers" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-4 text-gray-300">
                <p>
                  {language === 'en' 
                    ? 'When danger, violence, and chaos erupts in the streets, the children come running to The Safe House Rescue Center, desperate for safety and protection.'
                    : 'Cuando el peligro, la violencia y el caos estallan en las calles, los niños vienen corriendo al Centro de Rescate The Safe House, desesperados por seguridad y protección.'}
                </p>
                <p className="font-semibold text-lg text-colombia-yellow">
                  {language === 'en'
                    ? 'This is the reality they grow up in — where violence echoes through their community and fear is familiar.'
                    : 'Esta es la realidad en la que crecen — donde la violencia resuena en su comunidad y el miedo es familiar.'}
                </p>
                <p>
                  {language === 'en'
                    ? 'The Safe House is more than a center; it is their refuge, their shield, the place they run to when the world around them feels unsafe.'
                    : 'The Safe House es más que un centro; es su refugio, su escudo, el lugar al que corren cuando el mundo a su alrededor se siente inseguro.'}
                </p>
              </div>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
              <img
                src="https://customer-assets.emergentagent.com/job_3f3927fb-0e86-4c96-bb22-87f278b0fa3e/artifacts/9unnmcna_poor%20kids%20pic.webp"
                alt="Children in poverty seeking safety"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* See the Transformation - Videos */}
      <section className="pt-10 pb-12 bg-sh-dark" data-testid="impact-stats-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
              {language === 'en' ? 'See the ' : 'Mira la '}<span className="text-colombia-yellow">{language === 'en' ? 'Transformation' : 'Transformación'}</span>
            </h2>
            <p className="text-sm text-gray-400 mt-2">
              {language === 'en' ? 'Real stories. Real children. Real lives being changed.' : 'Historias reales. Niños reales. Vidas reales siendo cambiadas.'}
            </p>
          </div>

          <div className="max-w-3xl mx-auto flex justify-center">
            <div className="relative group">
              <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500"></div>
              <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-3 rounded-2xl shadow-2xl">
                <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-xl">
                  <div className="bg-gray-950 p-1.5 rounded-xl">
                    <div className="rounded-lg overflow-hidden w-72 md:w-[22rem]">
                      <HdVideo uid="ff3fec8942b3c95cea1b8380c61799a9" testId="featured-video-main" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/media"
              className="inline-flex items-center px-8 py-4 bg-colombia-yellow text-gray-900 rounded-full font-semibold text-lg hover:bg-amber-400 transition-colors"
              data-testid="explore-stories-button"
            >
              {language === 'en' ? 'Explore Video Library' : 'Explorar Biblioteca de Videos'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* YOU ARE THE HERO Section */}
      <section className="relative py-20 md:py-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-colombia-yellow/10 rounded-full filter blur-[150px]"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/10 rounded-full filter blur-[120px]"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-colombia-yellow/20 border border-colombia-yellow/30 rounded-full mb-8">
              <span className="text-2xl">🦸</span>
              <span className="text-colombia-yellow font-bold tracking-wider uppercase text-sm">{language === 'en' ? 'The Real Heroes' : 'Los Verdaderos Héroes'}</span>
            </div>
            
            {/* Main Headline */}
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6">
              {language === 'en' ? 'Without' : 'Sin'} <span className="text-transparent bg-clip-text bg-gradient-to-r from-colombia-yellow via-amber-400 to-yellow-300">{language === 'en' ? 'You' : 'Ti'}</span>,
              <br />
              {language === 'en' ? 'None of This is Possible.' : 'Nada de Esto es Posible.'}
            </h2>
            
            {/* Subtext */}
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
              {language === 'en' 
                ? <>Every child rescued. Every meal served. Every future transformed. <span className="text-white font-semibold">It happens because someone chose to act.</span></>
                : <>Cada niño rescatado. Cada comida servida. Cada futuro transformado. <span className="text-white font-semibold">Sucede porque alguien eligió actuar.</span></>}
            </p>
            
            {/* Hero Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 group">
                <div className="text-5xl mb-4">💛</div>
                <h3 className="text-xl font-bold text-white mb-2">{language === 'en' ? 'Monthly Partners' : 'Socios Mensuales'}</h3>
                <p className="text-gray-400 text-sm">{language === 'en' ? 'Your consistent support provides stability and hope that children can count on.' : 'Tu apoyo constante proporciona estabilidad y esperanza con la que los niños pueden contar.'}</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 group">
                <div className="text-5xl mb-4">🙏</div>
                <h3 className="text-xl font-bold text-white mb-2">{language === 'en' ? 'Prayer Warriors' : 'Guerreros de Oración'}</h3>
                <p className="text-gray-400 text-sm">{language === 'en' ? 'Your prayers cover these children in protection and guide every decision we make.' : 'Tus oraciones cubren a estos niños con protección y guían cada decisión que tomamos.'}</p>
              </div>
              
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 group">
                <div className="text-5xl mb-4">📢</div>
                <h3 className="text-xl font-bold text-white mb-2">{language === 'en' ? 'Advocates' : 'Promotores'}</h3>
                <p className="text-gray-400 text-sm">{language === 'en' ? 'Your voice spreads awareness and brings more heroes into the mission.' : 'Tu voz difunde conciencia y trae más héroes a la misión.'}</p>
              </div>
            </div>
            
            {/* Video D2F8E1E4 between Advocates and Powerful Statement - Portrait video */}
            <div className="mb-16 flex justify-center">
              <div className="relative group flex-shrink-0">
                <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500"></div>
                <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-3 rounded-2xl shadow-2xl">
                  <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-xl">
                    <div className="bg-gray-950 p-1.5 rounded-xl">
                      <div className="rounded-lg overflow-hidden w-72 md:w-[22rem]">
                        <HdVideo uid="417d71eb0f08c862a467882d6cb6d6f2" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Video — AI */}
            <div className="-mt-8 mb-12 flex justify-center">
              <div className="relative group flex-shrink-0">
                <div className="absolute -inset-4 bg-black/20 rounded-3xl blur-2xl group-hover:bg-black/30 transition-all duration-500"></div>
                <div className="relative bg-gradient-to-b from-gray-800 via-gray-900 to-gray-950 p-3 rounded-2xl shadow-2xl">
                  <div className="bg-gradient-to-b from-white/20 via-white/5 to-white/15 p-px rounded-xl">
                    <div className="bg-gray-950 p-1.5 rounded-xl">
                      <div className="rounded-lg overflow-hidden w-80 md:w-[28rem]">
                        <HdVideo uid="8ef7efb8ed983fa9f7e6c5632250614b" aspectClass="aspect-video" testId="video-ai-homepage" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Powerful Statement */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-colombia-yellow/20 via-amber-500/10 to-colombia-yellow/20 rounded-3xl blur-xl"></div>
              <div className="relative bg-gradient-to-r from-colombia-yellow via-amber-400 to-yellow-400 rounded-2xl p-8 md:p-12">
                <p className="text-2xl md:text-3xl lg:text-4xl font-black text-gray-900 leading-tight mb-4">
                  {language === 'en' ? "You're not just giving, you're becoming a hero!" : '¡No solo estás dando, te estás convirtiendo en un héroe!'}
                </p>
                <p className="text-base md:text-xl text-gray-800 mb-6">
                  {language === 'en' 
                    ? <>In a child's eyes, you're the one who gave them their childhood back...<br /><br />The one who stepped in<br />when no one else did.<br /><br />The one who changed<br />their story forever.</>
                    : <>A los ojos de un niño, tú eres quien les devolvió su infancia...<br /><br />Quien intervino<br />cuando nadie más lo hizo.<br /><br />Quien cambió<br />su historia para siempre.</>}
                </p>
                <div className="flex justify-center">
                  <Link
                    to="/heros"
                    className="px-8 py-4 bg-gray-900 text-white rounded-full font-bold text-lg hover:bg-gray-800 transition-all shadow-xl flex items-center justify-center gap-2"
                  >
                    <Heart className="w-5 h-5" fill="currentColor" />
                    {language === 'en' ? 'SUPER HERO DETAILS' : 'DETALLES DE SUPER HÉROE'}
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Bottom Quote */}
            <p className="mt-6 text-lg text-gray-400 italic flex items-center justify-center min-h-[80px]">
              {language === 'en' 
                ? '"A hero is someone who has given their life to something bigger than themselves."'
                : '"Un héroe es alguien que ha dado su vida por algo más grande que sí mismo."'}
            </p>
          </div>
        </div>
      </section>

      {/* TEMPORARILY HIDDEN - Coffee section hidden per user request, restore when ready */}
      {/* <section className="py-20 bg-gray-900 text-white" data-testid="coffee-cta-section">
        <div className="container mx-auto px-4">
          <AnimatedHeading
            level="h2"
            className="text-4xl md:text-5xl text-white mb-12"
            variant="slide"
            center={true}
            accent={true}
          >
            {language === 'en' ? 'A Cup That Changes Lives' : 'Una Taza Que Cambia Vidas'}
          </AnimatedHeading>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Coffee className="w-12 h-12 text-amber-400" />
              </div>
              <p className="text-xl text-gray-300 mb-6">
                {language === 'en'
                  ? <>Safe House Coffee is premium Colombian coffee where <strong>100% of the proceeds</strong> support the children at The Safe House Rescue Center.</>
                  : <>Safe House Coffee es café colombiano premium donde el <strong>100% de las ganancias</strong> apoya a los niños del Centro de Rescate The Safe House.</>}
              </p>
              <ul className="space-y-3 mb-8 text-gray-300">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 mr-2 mt-1 text-amber-400 flex-shrink-0" />
                  <span>{language === 'en' ? 'Every bag funds meals, education, medicine, and celebrations' : 'Cada bolsa financia comidas, educación, medicina y celebraciones'}</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 mr-2 mt-1 text-amber-400 flex-shrink-0" />
                  <span>{language === 'en' ? 'Not a portion. Not a percentage. Every dollar.' : 'No una parte. No un porcentaje. Cada dólar.'}</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 mr-2 mt-1 text-amber-400 flex-shrink-0" />
                  <span>{language === 'en' ? 'Premium 100% Arabica beans from Colombian highlands' : 'Granos Arábica 100% premium de las tierras altas colombianas'}</span>
                </li>
              </ul>
              <Link
                to="/coffee"
                className="inline-flex items-center px-8 py-4 bg-amber-500 text-gray-900 rounded-full font-semibold text-lg hover:bg-amber-400 transition-all duration-300 transform hover:scale-105"
                data-testid="shop-coffee-button"
              >
                {language === 'en' ? 'Shop Coffee' : 'Comprar Café'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_3f3927fb-0e86-4c96-bb22-87f278b0fa3e/artifacts/c2snw982_Coffee%20and%20sunrise%20over%20Colombian%20mountains.png"
                alt="Colombian Coffee at Sunrise"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section> */}

      {/* Final CTA - This Is the Moment */}
      <section className="relative py-28 overflow-hidden" data-testid="final-cta-section">
        {/* Background with dramatic gradient and overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-sh-dark to-black"></div>
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1920')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50"></div>
        
        {/* Animated accent lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-colombia-yellow to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-colombia-yellow to-transparent"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          {/* Main headline */}
          <AnimatedHeading
            level="h2"
            className="text-5xl md:text-7xl text-white mb-8 font-black"
            variant="slide"
            center={true}
            accent={true}
          >
            {language === 'en' ? 'The Time to Act Is Now.' : 'El Momento de Actuar Es Ahora.'}
          </AnimatedHeading>
          
          {/* Subheadline */}
          <p className="text-2xl md:text-3xl mb-4 text-gray-200 max-w-3xl mx-auto font-light">
            {language === 'en' ? 'Join us in this Mission.' : 'Únete a nosotros en esta Misión.'}
          </p>
          
          {/* Call to action text */}
          <p className="text-3xl md:text-4xl mb-12 text-colombia-yellow font-bold">
            {language === 'en' ? 'Together we can bring hope, safety, and restoration to these children.' : 'Juntos podemos llevar esperanza, seguridad y restauración a estos niños.'}
          </p>
          
          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/donate"
              className="group px-12 py-5 bg-colombia-yellow text-gray-900 rounded-full font-black text-xl hover:shadow-[0_0_40px_rgba(252,209,22,0.5)] transition-all duration-300 transform hover:scale-105"
              data-testid="final-donate-button"
            >
              {language === 'en' ? 'Donate Now' : 'Donar Ahora'}
              <ArrowRight className="inline-block ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/get-involved"
              className="px-12 py-5 bg-transparent text-white rounded-full font-bold text-xl hover:bg-white/10 transition-all duration-300 border-2 border-white/50 hover:border-white"
              data-testid="get-involved-button"
            >
              {language === 'en' ? 'Get Involved' : 'Involúcrate'}
            </Link>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal 
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        video={selectedVideo}
      />
    </div>
  );
};

export default Homepage;
